// The printable invoice, for both audiences.
//
// Two thin pages around one document: the lab reads through the staff API, the clinic through the
// portal API (which scopes to their own doctor group server-side). The sheet itself is identical,
// so what a doctor prints is exactly what the lab can reproduce.
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { WfInvoice } from '@alignova/shared';
import { InvoicePrint } from '../../ui/InvoicePrint.tsx';
import { Skeleton } from '../../ui/mobile.tsx';
import { IconArrowLeft } from '../../ui/icons.tsx';
import { wfApi } from '../workflow/workflowApi.ts';
import { portalApi } from '../portal/portalApi.ts';

function PrintView({
  load,
  backTo,
  backLabel,
}: {
  load: (id: string) => Promise<WfInvoice>;
  backTo: string;
  backLabel: string;
}) {
  const { id = '' } = useParams();
  const [invoice, setInvoice] = useState<WfInvoice | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    load(id)
      .then(setInvoice)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [id, load]);

  if (err) {
    return (
      <div className="mx-auto max-w-lg py-10 text-center">
        <p className="text-sm text-status-urgent-fg">{err}</p>
        <Link to={backTo} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
          <IconArrowLeft size={16} />
          {backLabel}
        </Link>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="mx-auto max-w-[820px] space-y-3 py-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  return (
    <div className="py-2">
      <Link
        to={backTo}
        className="no-print mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent"
      >
        <IconArrowLeft size={16} />
        {backLabel}
      </Link>
      <InvoicePrint invoice={invoice} />
    </div>
  );
}

export function StaffInvoicePrintPage() {
  return <PrintView load={(id) => wfApi.invoice(id)} backTo="/finance/invoices" backLabel="All invoices" />;
}

export function PortalInvoicePrintPage() {
  return <PrintView load={(id) => portalApi.invoice(id)} backTo="/portal/invoices" backLabel="My invoices" />;
}
